import { graphql, type DocumentType } from 'app/generated/gql'

/**
 * Queries behind the super-user Ops tab.
 *
 * The two `opsVoiceAgent*` metrics queries live on the ops schema (`/graphql/ops`),
 * so callers pass `opsClient`. `callData` is on the public schema.
 */

export const OPS_VOICE_AGENT_LOCATION_METRICS_QUERY = graphql(`
  query OpsVoiceAgentLocationMetrics($input: VoiceAgentLocationMetricsInput!) {
    opsVoiceAgentLocationMetrics(input: $input) {
      rows {
        locationId
        locationName
        workspaceId
        workspaceName
        aiCalls
        newPatientCalls
        appointmentsBooked
        appointmentsCompleted
        toolErrorCalls
        toolTimeoutCalls
        toolDuplicateCalls
        failedCalls
        llmFailedCalls
        llmSlowCalls
        disconnectedCalls
        maxDurationCalls
        errorCalls
        unsuccessfulCalls
        gptFailedCalls
        elevenLabsFailedCalls
      }
      total {
        locationId
        aiCalls
        newPatientCalls
        appointmentsBooked
        appointmentsCompleted
        toolErrorCalls
        toolTimeoutCalls
        toolDuplicateCalls
        failedCalls
        llmFailedCalls
        llmSlowCalls
        disconnectedCalls
        maxDurationCalls
        errorCalls
        unsuccessfulCalls
        gptFailedCalls
        elevenLabsFailedCalls
      }
      compareRows {
        locationId
        newPatientCalls
        appointmentsBooked
      }
      compareTotal {
        locationId
        newPatientCalls
        appointmentsBooked
      }
    }
  }
`)

export const OPS_VOICE_AGENT_DAILY_METRICS_QUERY = graphql(`
  query OpsVoiceAgentDailyMetrics($input: VoiceAgentDailyMetricsInput!) {
    opsVoiceAgentDailyMetrics(input: $input) {
      daily {
        date
        aiCalls
        failedCalls
        gptFailedCalls
        elevenLabsFailedCalls
      }
      locations {
        locationId
        lastCallBeforeRange
        days {
          date
          aiCalls
        }
      }
    }
  }
`)

/**
 * Per-call ElevenLabs results for the call lists, joined to the AI call activities
 * on `CallData.callId` = `Activity.typeId`.
 *
 * The selection leaves out `CallData.data`, which holds the whole ElevenLabs
 * post-call payload: a page of those is megabytes, and only the AI Call Details
 * drawer needs it (it loads one call by id).
 */
export const OPS_VOICE_AGENT_CALL_DATA_QUERY = graphql(`
  query OpsVoiceAgentCallData($search: String, $pagination: PaginationInput, $sort: SortInput) {
    callData(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        callId
        locationId
        callDate
        duration
        extractedData
        toolError
        toolTimeout
        toolDuplicate
        terminationReason
        errorCode
        errorReason
        callSuccessful
        noBookingReason
      }
      total
      page
    }
  }
`)

export type VoiceAgentLocationMetricsData = DocumentType<typeof OPS_VOICE_AGENT_LOCATION_METRICS_QUERY>
export type VoiceAgentDailyMetricsData = DocumentType<typeof OPS_VOICE_AGENT_DAILY_METRICS_QUERY>
export type VoiceAgentCallDataRow = NonNullable<
  DocumentType<typeof OPS_VOICE_AGENT_CALL_DATA_QUERY>[`callData`][`hits`]
>[number]
